import type { Input } from "../../../../../api/call";
import type { Result } from "../../../../../lib/patterns/result";
import { Err, Ok, isResultErr } from "../../../../../lib/patterns/result";
import { indexByFunction } from "diagnostics-lib/iterable";
import type { FILE_PARSING_ERROR } from "../../../common/file";
import { parseFile } from "../../../common/file";
import type { Parser, ParsingError, ParsingErrorMessageMap, ParsingResult } from "../../../types";
import {
  columns,
  columnsMandatory,
  hasInvalidColumns,
  hasInvalidFileEmpty,
  hasInvalidQuantityNameEmpty,
  hasInvalidSourceQuantity,
  hasMinSuperiorToMax,
  hasNegativeMaxStd,
  hasNotNumberValue,
  hasUnexpectedQuantityName,
  isValuesNotNull,
} from "./rules";
import parseCSV from "../../../../../lib/csv";

/**
 * @description Data that can be parsed from a cross filter file.
 */
export type CrossFilterParsingResultData = Input<
  "POST",
  "/projects/:projectId(uuid)/quantities/crossFiltersMetrology"
>["body"]["crossFilters"];
export type CrossFilterParsingResult = ParsingResult<CrossFilterParsingResultData> & {
  hasSimpleMetrology: boolean;
};

/**
 * @description Error that can be thrown by the input model parser.
 */
export enum CROSS_FILTER_PARSING_ERROR {
  FILE_INVALID_CONTENT = "FILE_INVALID_CONTENT",
  FILE_EMPTY = "FILE_EMPTY",
  DATA_FILE_EMPTY = "DATA_FILE_EMPTY",
  MIN_SUPERIOR_TO_MAX = "MIN_SUPERIOR_TO_MAX",
  MAX_STD_NEGATIVE = "MAX_STD_NEGATIVE",
  NOT_NUMBER_VALUES = "NOT_NUMBER_VALUES",
  VALUE_ALL_NULL = "VALUE_ALL_NULL",
  NOT_FOUND_INVALIDATED_QUANTITY = "NOT_FOUND_INVALIDATED_QUANTITY",
  NOT_FOUND_SOURCE_QUANTITY = "NOT_FOUND_SOURCE_QUANTITY",
  DATA_QUANTITY_DUPLICATED = "DATA_QUANTITY_DUPLICATED",
  DATA_SOURCE_QUANTITY_EMPTY = "DATA_SOURCE_QUANTITY_EMPTY",
  DATA_INVALIDATED_QUANTITY_EMPTY = "DATA_INVALIDATED_QUANTITY_EMPTY",
  ONLY_IMPORT_AND_METROLOGY_FILTERS = "ONLY_IMPORT_AND_METROLOGY_FILTERS",
  CROSS_FILTER_BY_PASS = "CROSS_FILTER_BY_PASS",
  INVALID_NUMBER_MINIMUM_VALUE = "INVALID_NUMBER_MINIMUM_VALUE",
  INVALID_NUMBER_MAXIMUM_VALUE = "INVALID_NUMBER_MAXIMUM_VALUE",
  INVALID_NUMBER_MAX_STD_VALUE = "INVALID_NUMBER_MAX_STD_VALUE",
  INVALID_SOURCE_QUANTITY = "INVALID_SOURCE_QUANTITY",
}
export type CrossFilterParsingErrorMessageMap = ParsingErrorMessageMap<
  CROSS_FILTER_PARSING_ERROR | FILE_PARSING_ERROR
>;
export type CrossFilterParsingError = ParsingError<CROSS_FILTER_PARSING_ERROR | FILE_PARSING_ERROR>;

/**
 * @description Parse input model file content.
 * @param file Input model file to parse
 * @returns
 */
export const parse: Parser<
  CrossFilterParsingResult,
  CrossFilterParsingError[],
  { expectedQuantityNameValues: { name: string; formula: string | null }[] }
> = async (file, { expectedQuantityNameValues }) => {
  /**
   * @description Check for common file issue.
   */
  const errors: CrossFilterParsingError[] = [];
  const fileResult = await parseFile(file, {
    expectedMimeType: ["text/csv", "application/vnd.ms-excel"],
  });

  /** Have to stop here, to handle file error */
  if (isResultErr(fileResult)) {
    errors.push(fileResult.value);
    return Err(errors);
  }

  const csv = await file.text();
  const dataLithum = parseCSV(csv, "line");

  /**
   * Get file content.
   */
  const content = fileResult.value as string;

  /**
   * Formatted file content.
   */
  const [header, ...data] = content.split("\n").map(line =>
    line
      .replace(/\r/, "")
      .split(";")
      .map(v => v.replaceAll('"', "")),
  );

  /**
   * We focus on a single column naming without taking into account the different versions between Lithium and Helium.
   */
  const columnHeaderNormalizes = columns.flatMap(([col]) => col ?? []);
  const targetData = dataLithum.map(line =>
    columnHeaderNormalizes.reduce<{ [key: string]: string | undefined }>((acc, columnName) => {
      if (columnName in acc && acc[columnName] !== undefined && acc[columnName] !== null) {
        return acc;
      }
      return {
        ...acc,
        [columnName]: line[columnName] ?? undefined,
      };
    }, {}),
  );

  /**
   * Check Format
   */
  if (hasInvalidColumns(header)) {
    errors.push({
      kind: CROSS_FILTER_PARSING_ERROR.FILE_INVALID_CONTENT,
      explain: "file don't contains expected columns",
      location: { file: file.name, line: 0 },
      data: {
        hint: columnsMandatory.join(", "),
        value: header?.join(", "),
      },
    });
  }

  /**
   * @description Check by line data.
   * @see {parseData}
   */
  const dataResult = parseData(file, targetData, expectedQuantityNameValues);
  if (isResultErr(dataResult)) errors.push(...dataResult.value);

  /**
   * Check global data.
   */
  const quantityIds = targetData
    .map(({ invalidatedQuantity, source } = {}) => ({ invalidatedQuantity, source }))
    .filter(Boolean);
  const values = new Set();
  for (const { invalidatedQuantity, source } of quantityIds) {
    if (values.has(`${invalidatedQuantity}/${source}`)) {
      errors.push({
        kind: CROSS_FILTER_PARSING_ERROR.DATA_QUANTITY_DUPLICATED,
        explain: "file contain duplicated row",
        location: { file: file.name, line: 0 },
        data: {
          key: invalidatedQuantity,
          hint: `${invalidatedQuantity}/${source}`,
        },
      });
      break;
    }
    values.add(`${invalidatedQuantity}/${source}`);
  }

  if (hasInvalidFileEmpty(targetData))
    errors.push({
      kind: CROSS_FILTER_PARSING_ERROR.DATA_FILE_EMPTY,
      explain: "empty data file",
      location: { file: file.name, line: 0 },
      data: {
        value: data.length,
      },
    });

  if (hasInvalidFileEmpty(dataResult.value))
    errors.push({
      kind: CROSS_FILTER_PARSING_ERROR.CROSS_FILTER_BY_PASS,
      explain: "",
      location: { file: file.name, line: 0 },
      data: {
        value: data.length,
      },
    });

  /** if any errors, return them, otherwise return parser result */
  return errors.length > 0
    ? Err(errors)
    : Ok({
        data: dataResult.value as CrossFilterParsingResultData,
        hasSimpleMetrology: targetData.length !== dataResult.value.length,
      });
};

interface CrossFilter {
  invalidatedQuantityName: string;
  evaluatedQuantityName: string;
  min: number | null;
  max: number | null;
  maxStd: number | null;
}

const parseData = (
  file: File,
  data: {
    [key: string]: string | undefined;
  }[],
  expectedNameValues: { name: string; formula: string | null }[],
): Result<CrossFilterParsingResultData, CrossFilterParsingError[]> => {
  const errors: CrossFilterParsingError[] = [];
  const crossFilters: CrossFilter[] = [];
  const quantityNames = expectedNameValues.map(({ name }) => name);
  const indexQuantityByName = indexByFunction(expectedNameValues, ({ name }) => name);

  for (let line = 0; line < data.length; line++) {
    const {
      invalidatedQuantity = "",
      source = "",
      minimumValue = "",
      maximumValue = "",
      maximumStd = "",
    } = data[line] || {};

    if (invalidatedQuantity === source) {
      // Metrology filter simple
      continue;
    }

    if (hasInvalidQuantityNameEmpty(invalidatedQuantity)) {
      errors.push({
        kind: CROSS_FILTER_PARSING_ERROR.DATA_INVALIDATED_QUANTITY_EMPTY,
        explain: "The invalidated quantity is empty",
        location: { file: file.name, line },
        data: {
          key: "invalidatedQuantity",
          hint: invalidatedQuantity,
          value: quantityNames.join(", "),
        },
      });
      continue;
    }

    if (hasInvalidQuantityNameEmpty(source)) {
      errors.push({
        kind: CROSS_FILTER_PARSING_ERROR.DATA_SOURCE_QUANTITY_EMPTY,
        explain: "The source quantity is empty",
        location: { file: file.name, line },
        data: {
          key: "source",
          hint: source,
          value: source,
        },
      });
    }

    if (hasUnexpectedQuantityName(invalidatedQuantity, quantityNames)) {
      errors.push({
        kind: CROSS_FILTER_PARSING_ERROR.NOT_FOUND_INVALIDATED_QUANTITY,
        explain: "unknown invalidatedQuantity",
        location: { file: file.name, line },
        data: {
          key: "invalidatedQuantity",
          hint: `${invalidatedQuantity}/${source}`,
          value: quantityNames.join(", "),
        },
      });
    }

    if (hasUnexpectedQuantityName(source, quantityNames)) {
      errors.push({
        kind: CROSS_FILTER_PARSING_ERROR.NOT_FOUND_SOURCE_QUANTITY,
        explain: "unknown source",
        location: { file: file.name, line },
        data: {
          key: "source",
          hint: `${invalidatedQuantity}/${source}`,
          value: quantityNames.join(", "),
        },
      });
    }

    if (hasInvalidSourceQuantity(maximumStd, source, indexQuantityByName)) {
      errors.push({
        kind: CROSS_FILTER_PARSING_ERROR.INVALID_SOURCE_QUANTITY,
        explain: "",
        location: { file: file.name, line },
        data: {
          key: "source",
          hint: `${invalidatedQuantity}/${source}`,
          value: maximumStd,
        },
      });
    }

    if (hasMinSuperiorToMax(minimumValue, maximumValue)) {
      errors.push({
        kind: CROSS_FILTER_PARSING_ERROR.MIN_SUPERIOR_TO_MAX,
        explain: "minimumValue is superior to maximumValue",
        location: { file: file.name, line },
        data: {
          key: "minimumValue",
          hint: `${invalidatedQuantity}/${source}`,
          value: minimumValue,
        },
      });
    }

    if (hasNegativeMaxStd(maximumStd)) {
      errors.push({
        kind: CROSS_FILTER_PARSING_ERROR.MAX_STD_NEGATIVE,
        explain: "standard deviation is negative",
        location: { file: file.name, line },
        data: {
          key: invalidatedQuantity,
          hint: `${invalidatedQuantity}/${source}`,
          value: maximumStd,
        },
      });
    }

    if (hasNotNumberValue(minimumValue)) {
      errors.push({
        kind: CROSS_FILTER_PARSING_ERROR.INVALID_NUMBER_MINIMUM_VALUE,
        explain: "values are not numbers",
        location: { file: file.name, line },
        data: {
          key: "minimumValue",
          hint: `${invalidatedQuantity}/${source}`,
          value: minimumValue,
        },
      });
    }
    if (hasNotNumberValue(maximumValue)) {
      errors.push({
        kind: CROSS_FILTER_PARSING_ERROR.INVALID_NUMBER_MAXIMUM_VALUE,
        explain: "values are not numbers",
        location: { file: file.name, line },
        data: {
          key: "maximumValue",
          hint: `${invalidatedQuantity}/${source}`,
          value: maximumValue,
        },
      });
    }
    if (hasNotNumberValue(maximumStd)) {
      errors.push({
        kind: CROSS_FILTER_PARSING_ERROR.INVALID_NUMBER_MAX_STD_VALUE,
        explain: "values are not numbers",
        location: { file: file.name, line },
        data: {
          key: "maximumStd",
          hint: `${invalidatedQuantity}/${source}`,
          value: maximumStd,
        },
      });
    }

    if (isValuesNotNull(maximumStd, maximumValue, minimumValue)) {
      errors.push({
        kind: CROSS_FILTER_PARSING_ERROR.VALUE_ALL_NULL,
        explain: "values are all null",
        location: { file: file.name, line },
        data: {
          key: "minimumValue",
          hint: `${invalidatedQuantity}/${source}`,
          value: `minimumValue:${minimumValue}-maximumValue:${maximumValue}-maximumStd:${maximumStd}`,
        },
      });
    }

    /**
     * Everything is fine, we can add the input model to the list.
     */
    crossFilters.push({
      invalidatedQuantityName: invalidatedQuantity,
      evaluatedQuantityName: source,
      min: minimumValue ? Number(minimumValue) : null,
      max: maximumValue ? Number(maximumValue) : null,
      maxStd: maximumStd ? Number(maximumStd) : null,
    });
  }

  return errors.length > 0 ? Err(errors) : Ok(crossFilters);
};
